import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import { getModule } from "vuex-module-decorators";
import AuthModule from "@/store/modules/AuthModule";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/resident/home",
    component: () => import("@/layouts/main-layout/MainLayout.vue"),
    meta: {
      middleware: "auth",
    },
    children: [
      {
        path: "/resident/home",
        name: "home",
        component: {
          template: "<div></div>",
        },
        meta: {
          pageTitle: "Accueil",
          breadcrumbs: ["Groupe Santé Nadon"],
        },
      },
      {
        path: "/admin/documents",
        name: "documents",
        component: () => import("@/views/admin/documents/DocumentsList.vue"),
        meta: {
          pageTitle: "Documentation",
          roles: ["Administrator", "VoidChequeReviewer", "SuperAdministrator"],
        },
      },
      {
        path: "/admin/residents",
        name: "residents",
        component: () =>
          import("@/views/admin/residents/RegistrationsList.vue"),
        meta: {
          pageTitle: "Demandes de résidence",
          roles: ["Administrator", "VoidChequeReviewer", "SuperAdministrator"],
        },
      },
      {
        path: "/admin/residents/:id/profile",
        name: "residents-profile",
        component: () =>
          import("@/views/admin/residents/profile/ResidentProfile.vue"),
        meta: {
          pageTitle: "Fiche de l'adulte hébergé",
          breadcrumbs: [
            { title: "Demandes de résidence", route: "/admin/residents" },
          ],
          roles: ["Administrator", "VoidChequeReviewer", "SuperAdministrator"],
        },
        children: [
          {
            path: "registration",
            name: "resident-profile-registration",
            component: () =>
              import(
                "@/views/admin/residents/profile/ResidentProfileRegistration.vue"
              ),
            meta: {
              roles: [
                "Administrator",
                "VoidChequeReviewer",
                "SuperAdministrator",
              ],
            },
          },
          {
            path: "forms",
            name: "resident-profile-forms",
            component: () =>
              import(
                "@/views/admin/residents/profile/ResidentProfileForms.vue"
              ),
            meta: {
              roles: [
                "Administrator",
                "VoidChequeReviewer",
                "SuperAdministrator",
              ],
            },
          },
        ],
      },
      {
        path: "/resident/registration",
        name: "resident-registration",
        component: () =>
          import("@/views/residents/registrations/ResidentRegistration.vue"),
        meta: {
          pageTitle: "Mon inscription",
          roles: ["User"],
        },
      },
      {
        path: "/resident/forms",
        name: "resident-forms",
        component: () => import("@/views/residents/forms/ResidentForms.vue"),
        meta: {
          pageTitle: "Mes formulaires",
          roles: ["User"],
        },
      },
      {
        path: "/resident/documents",
        name: "resident-documents",
        component: () =>
          import("@/views/residents/documents/ResidentDocumentList.vue"),
        meta: {
          pageTitle: "Ma documentation",
          roles: ["User"],
        },
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/AuthLayout.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
        meta: {
          pageTitle: "Connectez-vous",
        },
      },
      {
        path: "/sign-up",
        name: "sign-up",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignUp.vue"),
        meta: {
          pageTitle: "Créer un compte",
        },
      },
      {
        path: "/forgot-password",
        name: "forgot-password",
        component: () =>
          import(
            "@/views/crafted/authentication/basic-flow/ForgotPassword.vue"
          ),
        meta: {
          pageTitle: "Mot de passe oublié",
        },
      },
      {
        path: "/reset-password",
        name: "reset-password",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/ResetPassword.vue"),
        meta: {
          pageTitle: "Réinitialisation du mot de passe",
        },
      },
      {
        path: "/confirm-email",
        name: "confirmation-email",
        component: () =>
          import(
            "@/views/crafted/authentication/basic-flow/AccountConfirmationEmail.vue"
          ),
        meta: {
          pageTitle: "Confirmation du courriel",
        },
      },
      {
        path: "/two-factor-verification",
        name: "two-factor-verification",
        component: () =>
          import(
            "@/views/crafted/authentication/basic-flow/TwoFactorVerification.vue"
          ),
        meta: {
          pageTitle: "Vérification en deux étapes",
        },
      },
      {
        path: "/authenticator-setup",
        name: "authenticator-setup",
        component: () =>
          import(
            "@/views/crafted/authentication/basic-flow/AuthenticatorSetup.vue"
          ),
        meta: {
          pageTitle: "Configuration de l'authentificateur",
        },
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/SystemLayout.vue"),
    children: [
      {
        // the 404 route, when none of the above matches
        path: "/404",
        name: "404",
        component: () => import("@/views/crafted/authentication/Error404.vue"),
        meta: {
          pageTitle: "Error 404",
        },
      },
      {
        path: "/500",
        name: "500",
        component: () => import("@/views/crafted/authentication/Error500.vue"),
        meta: {
          pageTitle: "Error 500",
        },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const authModule = getModule(AuthModule, store);

router.beforeEach(async (to, from, next) => {
  // current page view title
  document.title = `${to.meta.pageTitle} - ${process.env.VUE_APP_NAME}`;

  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);

  // before page access check if page requires authentication
  if (to.meta.middleware == "auth") {
    let isAuthenticated = authModule.isUserAuthenticated;

    // If not authenticated (token missing or expired), attempt refresh
    if (!isAuthenticated) {
      try {
        isAuthenticated = await store.dispatch(
          "AuthModule/" + Actions.REFRESH_TOKEN
        );
      } catch {
        isAuthenticated = false;
      }
    }

    if (isAuthenticated) {
      // Redirect home page based on user role
      if (to.name === "home") {
        const userRoles = (authModule.currentUser as any)?.roles || [];
        const isPartOfAdminGroup = userRoles.some((r) =>
          [
            "Administrator",
            "SuperAdministrator",
            "VoidChequeReviewer",
          ].includes(r)
        );
        next({
          name: isPartOfAdminGroup ? "residents" : "resident-registration",
        });
        return;
      }

      const requiredRoles = to.meta.roles as string[];

      // If route requires roles
      if (requiredRoles && requiredRoles.length > 0) {
        const userRoles = (authModule.currentUser as any)?.roles || [];
        const hasRequiredRole = requiredRoles.some((role) =>
          userRoles.includes(role)
        );

        const isPartOfAdminGroup = userRoles.some((r) =>
          [
            "Administrator",
            "SuperAdministrator",
            "VoidChequeReviewer",
          ].includes(r)
        );

        if (hasRequiredRole) {
          next();
        } else if (isPartOfAdminGroup) {
          next({ name: "residents" });
        } else {
          next({ name: "resident-registration" });
        }
      } else {
        // No specific roles required, just authentication.
        next();
      }
    } else {
      store.commit(Mutations.PURGE_AUTH);
      next({ name: "sign-in", query: { redirect: to.fullPath } });
    }
  } else {
    next();
  }

  // Scroll page to top on every route change
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
});

export default router;
